import { useEffect, useState } from "react";
import { axiosJWTInstance } from "./axiosJWTInstance";
import { DateRange } from "../types/common";
import { MAX_SPAN_DAYS } from "../config/showRange";

const SAMPLE_SIZE = 200;

export interface DayCount {
  date: Date;
  count: number;
}

export interface Elsewhere {
  total: number;
  days: DayCount[];
  complete: boolean;
}

export interface PendingSplit {
  inPeriod: number;
  elsewhere: number;
  span: DateRange;
}

const pad = (value: number) => String(value).padStart(2, "0");
const toIso = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const summarize = (orders: any[], total: number): Elsewhere => {
  const byDay: { [day: string]: DayCount } = {};
  orders.forEach(order => {
    const date = startOfDay(new Date(order.orderDate));
    const day = toIso(date);
    byDay[day] = { date, count: byDay[day] ? byDay[day].count + 1 : 1 };
  });
  const days = Object.keys(byDay).sort().reverse().map(day => byDay[day]);
  return { total, days, complete: orders.length >= total };
};

export const findByDay = (entity: string, filter: any): Promise<Elsewhere | null> => {
  const today = startOfDay(new Date());
  const since = new Date(today.getFullYear(), today.getMonth(), today.getDate() - MAX_SPAN_DAYS);
  const params = new URLSearchParams({ entity, dateSince: toIso(since), dateUntil: toIso(today) });
  const query = { filter, skip: 0, take: SAMPLE_SIZE, sort: [{ field: "orderDate", dir: "desc" }] };
  return axiosJWTInstance
    .post(`/Orders?${params.toString()}`, query)
    .then(res => (res.data && res.data.total > 0 ? summarize(res.data.data || [], res.data.total) : null))
    .catch(() => null);
};

export const spanOf = (found: Elsewhere): DateRange => ({ start: found.days[found.days.length - 1].date, end: found.days[0].date });

export const dayLabel = (date: Date) =>
  date.toLocaleDateString("es-AR", date.getFullYear() === new Date().getFullYear()
    ? { day: "2-digit", month: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" });

export const rangeLabel = (range: DateRange) =>
  range.start.getTime() === range.end.getTime()
    ? `Ver el ${dayLabel(range.start)}`
    : `Ver del ${dayLabel(range.start)} al ${dayLabel(range.end)}`;

const isWithin = (date: Date, range: DateRange) => date >= startOfDay(range.start) && date <= startOfDay(range.end);

export const useElsewhere = (entity: string, filter: any, enabled: boolean, version: number = 0) => {
  const [found, setFound] = useState<Elsewhere | null>(null);

  useEffect(() => {
    setFound(null);
    if (!enabled) return undefined;
    let current = true;
    findByDay(entity, filter).then(result => {
      if (current) setFound(result);
    });
    return () => {
      current = false;
    };
  }, [entity, filter, enabled, version]);

  return found;
};

// GetIndicators no recibe rango ni filtros: cuenta todas las fechas, y la tabla sólo las del rango.
export const usePendingSplit = (entity: string, filter: any, count: number, range: DateRange): PendingSplit | null => {
  const found = useElsewhere(entity, filter, count > 0, count);
  if (!found) return null;
  const inPeriod = found.days.filter(day => isWithin(day.date, range)).reduce((sum, day) => sum + day.count, 0);
  const elsewhere = count - inPeriod;
  return elsewhere > 0 ? { inPeriod, elsewhere, span: spanOf(found) } : null;
};
